import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ImageAlternativeType, OrderableItem, OrderableQualifier, OrderedItem } from 'ngx-web-api';
import { filter } from 'rxjs/operators';
import { Subscription, noop } from 'rxjs';
import { BuilderService } from '../builder.service';
import { Coordinates } from '../domain/coordinates.model';
import { IngredientAction } from '../domain/ingredient-action.enum';
import { Rectangle } from '../domain/rectangle.model';
import { Resolution } from '../domain/resolution.model';
import { StageIngredientAction } from '../domain/stage-ingredient-action.model';
import { StageIngredientState } from '../domain/stage-ingredient-state.model';
import { StageIngredient } from '../domain/stage-ingredient.model';
import { StagePosition } from '../domain/stage-position.enum';
import { Router, NavigationStart } from '@angular/router';

@Component({
  selector: 'fts-pizza-builder',
  templateUrl: './pizza-builder.component.html',
  styleUrls: ['./pizza-builder.component.scss'],
})
export class PizzaBuilderComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  name: string;
  @Input()
  orderable: OrderableItem;
  @Input()
  ordered: OrderedItem;
  @Output()
  onCanvasNotSupported: EventEmitter<any> = new EventEmitter();

  @ViewChild('builderCanvas', { static: true })
  builderCanvas: ElementRef;

  private rctx: CanvasRenderingContext2D;
  private offScreenCanvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  private builderImageBase: HTMLImageElement;

  private stageState: Array<StageIngredientState> = [];
  private stageIngredients: Array<StageIngredient> = [];

  private CENTER_RECT: Rectangle = { x: 20, y: 20, width: 260, height: 260 };
  private LEFT_RECT: Rectangle = { x: 20, y: 20, width: 80, height: 260 };
  private RIGHT_RECT: Rectangle = { x: 150, y: 20, width: 100, height: 260 };

  private CANVAS_WIDTH = 300;
  private CANVAS_HEIGHT = 300;
  private BASE: Coordinates = { x: 0, y: 0 };
  private DEFAULT_STAGE_QUALIFIER = 10;
  private subscriptions: Subscription[];
  private imageLoadingCount = 0;

  constructor(private builderService: BuilderService, private router: Router) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['ordered'] && !!changes['ordered'].currentValue) {
      if (
        !changes['ordered'].firstChange &&
        (changes['ordered'].currentValue.itemId !== changes['ordered'].previousValue.itemId ||
          changes['ordered'].currentValue.item !== changes['ordered'].previousValue.item)
      ) {
        this.clearStage();
      }
      if (this.orderable.useBuilder && this.ordered.item === this.orderable.name) {
        setTimeout(() => this.builderService.restoreStageIngredients(changes['ordered'].currentValue, this.orderable), 1);
      }
    }
  }

  ngOnDestroy() {
    this.builderService.resetStageAction();
    if (!!this.subscriptions) {
      this.subscriptions.forEach(s => s.unsubscribe());
    }
  }

  ngOnInit() {
    this.offScreenCanvas = document.createElement('canvas');
    this.offScreenCanvas.width = this.CANVAS_WIDTH;
    this.offScreenCanvas.height = this.CANVAS_HEIGHT;
    this.ctx = this.offScreenCanvas.getContext('2d');

    this.initCanvas();

    this.subscriptions = [
      this.router.events
        .pipe(filter(event => event instanceof NavigationStart))
        .subscribe(() => this.builderService.resetStageAction(), noop),
      this.builderService.stageUpdate$
        .pipe(filter((update: StageIngredientAction) => !!update && update.action !== IngredientAction.RESET))
        .subscribe((update: StageIngredientAction) => {
          this.removePreviousStagedImageOfSameIngredient(update.stageIngredient);
          if (!!update.stageIngredient && !this.isStaged(update.stageIngredient)) {
            this.stageIngredients.push(update.stageIngredient);
            if (!!update.stageIngredient.image) {
              this.imageLoadingCount++;
              this.builderService.startBuilderImageLoading();
              update.stageIngredient.image.onload = () => this.handleStageUpdate(update);
            }
          } else {
            this.handleStageUpdate(update);
          }
        }, noop),
    ];
  }

  removePreviousStagedImageOfSameIngredient(stageIngredient: StageIngredient) {
    if (stageIngredient.imageType === ImageAlternativeType.BUILDER_REPEAT || !stageIngredient.image) {
      return;
    }
    const previousStaged = this.stageState.find(
      staged =>
        staged.ingredient.name === stageIngredient.name &&
        staged.ingredient.ordinal === stageIngredient.ordinal &&
        (staged.ingredient.src !== stageIngredient.src || staged.ingredient.stageQualifier !== stageIngredient.stageQualifier)
    );
    if (!!previousStaged) {
      this.removeIngredient(previousStaged.ingredient);
    }
  }

  isStaged(stageIngredient: StageIngredient): boolean {
    return this.stageIngredients.some(
      staged => staged.name === stageIngredient.name && staged.ordinal === stageIngredient.ordinal && staged.src === stageIngredient.src
    );
  }

  getStaged(update: StageIngredientAction): StageIngredient {
    return this.stageIngredients.find(
      staged =>
        staged.name === update.stageIngredient.name &&
        staged.ordinal === update.stageIngredient.ordinal &&
        staged.src === update.stageIngredient.src
    );
  }

  private handleStageUpdate(update: StageIngredientAction) {
    const stageIngredient = this.getStaged(update);
    if (!stageIngredient) {
      return;
    }
    if (!stageIngredient.stageQualifier) {
      stageIngredient.stageQualifier = this.DEFAULT_STAGE_QUALIFIER;
    }
    switch (update.action) {
      case IngredientAction.REMOVE: {
        this.updateLoadingStatus();
        this.removeIngredient(stageIngredient);
        break;
      }
      case IngredientAction.ADD: {
        this.updateLoadingStatus();
        this.includeIngredient(stageIngredient, StagePosition.ALL);
        break;
      }
      case IngredientAction.SELECT_HALF: {
        const currentQualifier = stageIngredient.stageQualifier;
        this.updateLoadingStatus();
        this.includeIngredient(stageIngredient, this.getPosition(update));
        if (currentQualifier !== this.DEFAULT_STAGE_QUALIFIER) {
          this.extraIngredient(stageIngredient, currentQualifier);
        }
        break;
      }
      case IngredientAction.ADD_QUALIFIER: {
        if (
          update.qualifier.recipeFactor === 0 ||
          StageIngredientAction.hasOrderedSideQualifier(update.orderableIngredient, update.orderedQualifierList)
        ) {
          this.updateLoadingStatus();
          return;
        }
        this.updateLoadingStatus();
        this.includeIngredient(stageIngredient, this.getPosition(update));
        const q = this.getStageQualifier(update.qualifier, stageIngredient.qualifierList);
        this.extraIngredient(stageIngredient, q);
        break;
      }
      case IngredientAction.REMOVE_QUALIFIER: {
        if (
          (update.qualifier.recipeFactor === 0 && update.qualifier.type !== 'Side') ||
          (update.orderedQualifierList?.length === 1 &&
            StageIngredientAction.hasOrderedSideQualifier(update.orderableIngredient, update.orderedQualifierList))
        ) {
          this.updateLoadingStatus();
          return;
        }
        this.updateLoadingStatus();
        const isRemovingSideQualifier = update.qualifier.type === 'Side' && !update.orderedQualifierList?.includes(update.qualifier.name);

        if (isRemovingSideQualifier) {
          const defaultStageIngredient = this.builderService.getStageIngredientFromOrderable(update);
          stageIngredient.src = defaultStageIngredient.src;
          stageIngredient.image = defaultStageIngredient.image;
          stageIngredient.imageType = defaultStageIngredient.imageType;
        }

        this.includeIngredient(stageIngredient, this.getPosition(update));

        if (isRemovingSideQualifier && update.orderedQualifierList?.length > 0) {
          const activeQualifier = update.orderableIngredient.qualifiers.find(q => q.name === update.orderedQualifierList[0]);
          if (activeQualifier) {
            const activeUpdate: StageIngredientAction = {
              ...update,
              qualifier: activeQualifier,
              action: IngredientAction.ADD_QUALIFIER,
            };
            const activeStageIngredient = this.builderService.getStageIngredientFromOrderable(activeUpdate);
            stageIngredient.src = activeStageIngredient.src;
            stageIngredient.image = activeStageIngredient.image;
            stageIngredient.imageType = activeStageIngredient.imageType;
            const q = this.getStageQualifier(activeQualifier, stageIngredient.qualifierList);
            this.extraIngredient(stageIngredient, q);
          }
        } else {
          const q = this.getStageQualifier(update.qualifier, stageIngredient.qualifierList);
          this.extraIngredient(stageIngredient, q);
        }
        break;
      }
      case IngredientAction.RESTORE: {
        this.updateLoadingStatus();
        this.restoreIngredient(stageIngredient, update);
        break;
      }
    }
  }

  private updateLoadingStatus() {
    if (--this.imageLoadingCount === 0) {
      this.builderService.stopBuilderImageLoading();
    }
    if (this.imageLoadingCount < 0) {
      this.imageLoadingCount = 0;
    }
  }

  private getPosition(action: StageIngredientAction): StagePosition {
    if (!action.left && !action.right) {
      return StagePosition.ALL;
    } else {
      if (action.left) {
        return StagePosition.LEFT;
      } else {
        return StagePosition.RIGHT;
      }
    }
  }

  private getStageQualifier(qualifier: OrderableQualifier, list?: OrderableQualifier[]): number {
    return this.DEFAULT_STAGE_QUALIFIER + qualifier.recipeFactor * this.DEFAULT_STAGE_QUALIFIER;
  }

  private getStageQualifierByName(qualifierName: string, stageIngredient: StageIngredient): number {
    const orderableQ = stageIngredient.qualifierList.find(q => q.name === qualifierName);
    return this.getStageQualifier(orderableQ, stageIngredient.qualifierList);
  }

  private initCanvas() {
    if (!this.builderCanvas) {
      this.onCanvasNotSupported.emit();
      return;
    } else {
      this.rctx = this.builderCanvas.nativeElement.getContext('2d');
      this.loadBaseImage();
    }
  }

  private loadBaseImage() {
    const builderImagePizzaBase = this.orderable.pictures.find(picture => picture.type === ImageAlternativeType.BUILDER_FIXED);
    if (!!builderImagePizzaBase) {
      this.builderImageBase = new Image();
      this.builderImageBase.src = builderImagePizzaBase.src;
      this.builderImageBase.onload = () => {
        this.updateStage();
      };
    }
  }

  private clearStage() {
    this.stageIngredients = [];
    this.stageState = [];
    if (!!this.ctx) {
      this.ctx.clearRect(0, 0, this.builderCanvas.nativeElement.width, this.builderCanvas.nativeElement.height);
      this.updateStage();
    }
  }

  private updateStage() {
    this.ctx.clearRect(0, 0, this.CANVAS_WIDTH, this.CANVAS_HEIGHT);
    this.rctx.clearRect(0, 0, this.CANVAS_WIDTH, this.CANVAS_HEIGHT);
    if (!!this.builderImageBase) {
      this.ctx.drawImage(this.builderImageBase, this.BASE.x, this.BASE.y);
    }

    this.stageState.sort((a, b) => {
      return a.ingredient.ordinal - b.ingredient.ordinal;
    });

    this.stageState.forEach(a => {
      if (a.ingredient.included) {
        this.addIngredientToStage(a.ingredient);
      }
    });

    this.render();
  }

  private render() {
    this.rctx.drawImage(this.offScreenCanvas, this.BASE.x, this.BASE.y);
  }

  private includeIngredient(ingredient: StageIngredient, position: StagePosition = StagePosition.ALL) {
    if (ingredient.included && ingredient.position === position) {
      return;
    } else if (ingredient.included && position !== StagePosition.DEFAULT) {
      this.removeIngredient(ingredient);
    }
    this.stageState.push({ ingredient: ingredient, coordinates: [] });
    ingredient.included = true;
    ingredient.position = position;

    this.updateStage();
  }

  private removeIngredient(ingredient: StageIngredient) {
    if (!ingredient.included) {
      return;
    }
    const ingStageIndex = this.stageState.findIndex(i => i.ingredient.name === ingredient.name);
    if (ingStageIndex !== -1) {
      this.stageState.splice(ingStageIndex, 1);
    }
    ingredient.included = false;
    ingredient.position = StagePosition.ALL;
    ingredient.stageQualifier = this.DEFAULT_STAGE_QUALIFIER;
    this.updateStage();
  }

  private extraIngredient(ingredient: StageIngredient, qualifier: number) {
    if (!ingredient.included) {
      ingredient.stageQualifier = this.DEFAULT_STAGE_QUALIFIER;
      return;
    }
    ingredient.stageQualifier = ingredient.stageQualifier === qualifier ? this.DEFAULT_STAGE_QUALIFIER : qualifier;
    this.updateStage();
  }

  private addIngredientToStage(ingredient: StageIngredient) {
    if (!ingredient.image || !ingredient.included || !ingredient.image.complete || ingredient.image.naturalWidth === 0) {
      return;
    }

    let placement = this.CENTER_RECT;
    if (ingredient.position === StagePosition.LEFT) {
      placement = this.LEFT_RECT;
    } else if (ingredient.position === StagePosition.RIGHT) {
      placement = this.RIGHT_RECT;
    }

    this.ctx.globalCompositeOperation = 'source-atop';
    if (ingredient.imageType !== ImageAlternativeType.BUILDER_REPEAT) {
      this.ctx.drawImage(ingredient.image, this.BASE.x, this.BASE.y);
    } else {
      this.randomPlacement(ingredient, placement, {
        w: Math.floor(ingredient.image.width / 2),
        h: Math.floor(ingredient.image.height / 2),
      });
    }
    this.ctx.globalCompositeOperation = 'source-over';

    this.ctx.restore();
  }

  private restoreIngredient(stageIngredient: StageIngredient, update: StageIngredientAction) {
    this.includeIngredient(stageIngredient, this.getPosition(update));
    if (!update.orderedQualifierList) {
      return;
    }
    update.orderedQualifierList.forEach(qualifierLabel => {
      const qf = this.getStageQualifierByName(qualifierLabel, stageIngredient);
      this.extraIngredient(stageIngredient, qf);
    });
  }

  private randomPlacement(ingredient: StageIngredient, stage: Rectangle, sprite: Resolution) {
    // Double num of sprites for full placement
    const qFactor = ingredient.position === StagePosition.ALL ? 2 : 1;
    // Get index of ingredient on stage array
    const ingredientIndexOnStage = this.stageState.findIndex(i => i.ingredient.name === ingredient.name);

    let restToAdd;
    // Determine if we need to increase the number of staged sprites on stage, based on qualifier and current number of staged objects
    const needToIncreasePortion: boolean =
      ingredient.stageQualifier * qFactor - this.stageState[ingredientIndexOnStage].coordinates.length > 0;

    // If there are already staged sprites of this ingredient
    if (this.stageState[ingredientIndexOnStage].coordinates.length > 0) {
      // And the qualifier was increased
      if (needToIncreasePortion) {
        // Calculate how many more we need
        restToAdd = ingredient.stageQualifier * qFactor - this.stageState[ingredientIndexOnStage].coordinates.length;
      } else {
        // If the qualifier was decreased, remove some sprites accordingly
        this.stageState[ingredientIndexOnStage].coordinates.splice(
          ingredient.stageQualifier * qFactor,
          this.stageState[ingredientIndexOnStage].coordinates.length - ingredient.stageQualifier * qFactor
        );
      }
      // Add the already rendered objects that will remain on stage
      this.stageState[ingredientIndexOnStage].coordinates.forEach(p => {
        this.ctx.drawImage(ingredient.image, p.x, p.y);
      });
    } else {
      // Add previously unselected ingredient
      restToAdd = ingredient.stageQualifier * qFactor;
    }

    if (!restToAdd) {
      return;
    }

    // Get random non-overlapping (determined by sprite w,h) positions for the rest to add sprites
    let placed = 0;
    let maxAttempts = ingredient.stageQualifier * 10;

    while (placed < restToAdd && maxAttempts > 0) {
      const x = Math.floor(Math.random() * stage.width + stage.x),
        y = Math.floor(Math.random() * stage.height + stage.y);
      let available = true;
      for (const point of this.stageState[ingredientIndexOnStage].coordinates) {
        if (Math.abs(point.x - x) < sprite.w && Math.abs(point.y - y) < sprite.h) {
          available = false;
          break;
        }
      }
      if (available) {
        this.stageState[ingredientIndexOnStage].coordinates.push({
          x: x,
          y: y,
        });
        this.ctx.drawImage(ingredient.image, x, y);
        placed += 1;
      }
      maxAttempts -= 1;
    }
  }
}
